// src/components/research/SourceCard.jsx
// Ausklappbare Vorschau-Karte für eine Recherche-Quelle (Web/Datei sowie manuell
// erfasste Bücher/Zeitschriften/Interviews). Wird in der Quellen-Suche (mit
// "Zuordnen") und unter einem Eintrag (mit "Entfernen") genutzt. Bei verknüpften
// Quellen kann eine Ergänzung zur Verknüpfung erfasst werden (z.B. "S. 41") —
// dafür `note` + `onNoteChange` übergeben.

import React, { useEffect, useState } from "react";

// Anzeige-Badges je Quellentyp; alles andere gilt als "Web".
const TYPE_BADGES = {
  file: "Datei",
  buch: "Buch",
  zeitschrift: "Zeitschrift",
  interview: "Interview",
};

const Thumb = ({ source }) => {
  const [errored, setErrored] = useState(false);
  const src = !errored ? source.thumbnailData || source.favicon || "" : "";
  if (!src) {
    return (
      <div className="source-thumb source-thumb--placeholder" aria-hidden="true">
        <span>SEITE</span>
      </div>
    );
  }
  return (
    <div className="source-thumb">
      <img
        src={src}
        alt=""
        loading="lazy"
        onError={() => setErrored(true)}
      />
    </div>
  );
};

const SourceCard = ({ source, onAssign, onRemove, assignDisabled, isAssigned, note, onNoteChange }) => {
  const [expanded, setExpanded] = useState(false);
  // Ergänzung: lokaler Entwurf, Persistenz bei Blur/Enter (nicht je Tastendruck).
  const [noteDraft, setNoteDraft] = useState(note || "");
  useEffect(() => setNoteDraft(note || ""), [note]);

  const isFile = source.type === "file" || source.kind === "file";
  const badgeKey = isFile ? "file" : TYPE_BADGES[source.type] ? source.type : "web";
  const badgeLabel = TYPE_BADGES[badgeKey] || "Web";

  const openWebsite = () => {
    if (source.url) window.open(source.url, "_blank", "noopener,noreferrer");
  };

  const commitNote = () => {
    if (onNoteChange && (noteDraft || "").trim() !== (note || "")) {
      onNoteChange(noteDraft.trim());
    }
  };

  return (
    <div className={`source-card${source.missing ? " source-card--missing" : ""}`}>
      <div className="source-card-head">
        <Thumb source={source} />
        <div className="source-card-meta">
          <div className="source-card-title" title={source.title}>
            <span className={`source-card-kind source-card-kind--${badgeKey}`}>
              {badgeLabel}
            </span>
            {source.title || "(ohne Titel)"}
          </div>
          {source.author && <div className="source-card-thema">{source.author}</div>}
          {source.thema && !isFile && <div className="source-card-thema">Thema: {source.thema}</div>}
          {source.url && (
            <div className="source-card-url" title={source.url}>
              {source.url}
            </div>
          )}
        </div>
        <button
          type="button"
          className="source-card-expand"
          title={expanded ? "Einklappen" : "Ausklappen"}
          aria-expanded={expanded}
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? "▾" : "▸"}
        </button>
      </div>

      {expanded && (
        <div className="source-card-body">
          {source.content ? (
            <p>{source.content}</p>
          ) : (
            <p className="source-card-empty">Keine Vorschau verfügbar.</p>
          )}
          {source.tags && <div className="source-card-tags">Tags: {source.tags}</div>}
        </div>
      )}

      {/* Ergänzung zur Verknüpfung (nur bei verknüpften Quellen editierbar) */}
      {onNoteChange && (
        <div className="source-card-note">
          <span className="source-card-note-label">Ergänzung:</span>
          <input
            value={noteDraft}
            placeholder='z.B. "S. 41" oder "3. Abschnitt"'
            onChange={(e) => setNoteDraft(e.target.value)}
            onBlur={commitNote}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                e.target.blur();
              } else if (e.key === "Escape") {
                e.preventDefault();
                setNoteDraft(note || "");
                e.target.blur();
              }
            }}
          />
        </div>
      )}
      {!onNoteChange && note && <div className="source-card-note source-card-note--readonly">Ergänzung: {note}</div>}

      <div className="source-card-actions">
        {source.url && (
          <button type="button" className="btn btn-secondary btn-sm" onClick={openWebsite}>
            {isFile ? "Datei öffnen" : "Website öffnen"}
          </button>
        )}
        {onAssign &&
          (isAssigned ? (
            <span className="source-card-assigned">✓ Zugeordnet</span>
          ) : (
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={assignDisabled}
              title={assignDisabled ? "Zuerst eine Frage bearbeiten" : "Dieser Frage zuordnen"}
              onClick={() => onAssign(source)}
            >
              Dieser Frage zuordnen
            </button>
          ))}
        {onRemove && (
          <button
            type="button"
            className="btn btn-danger btn-sm"
            onClick={() => onRemove(source)}
          >
            Entfernen
          </button>
        )}
      </div>
    </div>
  );
};

export default SourceCard;
